import { WikiArticleReviewDownloadDto, WikiColumnDownloadDto } from 'collaboration-service';
import ContentLanguageSelect from 'components/ContentLanguageSelect';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import SwitchButton from 'components/Switch/SwitchButton';
import Editor from "Dummies/TestImpl/TestEditor";
import { Button, ButtonGroup, Icon, LanguageSelect } from 'imaginarity-react-ui';
import * as React from 'react';
import { languages } from 'services/Config';
import { KeyOfType } from 'services/Helpers';
import { useImgI18N } from 'services/ImgI18N';
import { UploadFile } from './WikiEditArticle';
import WikiEditColumn from './WikiEditColumn';
import { WikiSC as T } from './WikiSC';

export type WikiActiveReviewPanelProps = {
    column: WikiColumnDownloadDto;
    review: WikiArticleReviewDownloadDto;
    referenceLanguage: string | undefined;
    contentLanguage: string | undefined;
    currentLanguage: string;
    namespace: string;
    refLanguageChanged: (lng: string) => void;
    availableLngs: string[];
    updateReviewColumn: (column: WikiColumnDownloadDto, uploadFile?: UploadFile | UploadFile[]) => void;
    toggleBoolOfReview: (what: KeyOfType<WikiArticleReviewDownloadDto, boolean>) => () => void;
    reasonIsSet: boolean;
    remarkText: string;
    setRemarkText: (text: string) => void;
    showRefLang: boolean;
    setShowRefLang: (val: boolean) => void;
    showCurrentReference: boolean;
    setShowCurrentReference: (val: boolean) => void;
};

const WikiActiveReviewPanel = (p: WikiActiveReviewPanelProps) => {
    const {
        column, review, referenceLanguage, contentLanguage, currentLanguage, namespace,
        refLanguageChanged, availableLngs, updateReviewColumn, toggleBoolOfReview,
        reasonIsSet, remarkText, setRemarkText,
        showRefLang, setShowRefLang, showCurrentReference, setShowCurrentReference,
    } = p;

    const { t, I18N, createT } = useImgI18N(namespace);
    const tGeneral = createT("general");

    return (
        <T.ActiveEditRevisionContainer>
            <T.ActiveEditRevisionTitle>
                <I18N text="show reference language" namespace='warranty' />
            </T.ActiveEditRevisionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content", alignItems: "center", columnGap: 10 }}>
                <SwitchButton isActive={() => setShowRefLang(!showRefLang)} floated='left' activeColor='@accentLightBlue' initialState={showRefLang} />
                {showRefLang ?
                    <>
                        <div style={{ paddingLeft: 30 }}>
                            <LanguageSelect
                                languageList={languages}
                                value={referenceLanguage ?? contentLanguage ?? currentLanguage}
                                onChange={refLanguageChanged}
                                translationFunction={tGeneral}
                            />
                        </div>
                        <ButtonGroup>
                            <Button cursor={showCurrentReference ? "default" : "pointer"} onClick={!showCurrentReference ? () => setShowCurrentReference(true) : undefined} kind={showCurrentReference ? "secondary" : "cancelButton"} content={"Current Version (PAG)"} icon="calendar" tooltip={!showCurrentReference ? { tooltipText: ("Display current version as reference from Group Garantie Conception") } : undefined} />
                            <Button cursor={!showCurrentReference ? "default" : "pointer"} onClick={showCurrentReference ? () => setShowCurrentReference(false) : undefined} kind={!showCurrentReference ? "secondary" : "cancelButton"} content={"Previous Year Version (Market)"} icon="history back" tooltip={showCurrentReference ? { tooltipText: ("Display previous year's version as reference from published Market Group") } : undefined} />
                        </ButtonGroup>
                    </>
                    : <div />
                }
            </div>

            <T.ActiveEditRevisionTitle>
                <I18N text="review language" namespace='warranty' />
            </T.ActiveEditRevisionTitle>
            <ContentLanguageSelect languages={availableLngs} />

            <T.ActiveEditRevisionTitle style={{ marginBottom: -3 }}>
                <I18N text="{{columntype}} content" data={{ columntype: column.columnType }} namespace='warranty' />
                <Button icon="info" kind="fullTransparentButton" floated='right' tooltip={{
                    tooltipText: "",
                    tooltipContent: <div style={{ textAlign: "left" }}><SafeHTML html={t('editing is only possible if <b>market change</b> or <b>translation edit</b> has been selected as review reason')} /></div>
                }} />
            </T.ActiveEditRevisionTitle>
            <div style={{ marginBottom: -3 }}>
                <WikiEditColumn
                    column={review!.columnChanges!}
                    columnCount={column.columnWidth}
                    columnChanged={updateReviewColumn}
                    showReference={showRefLang}
                    reviewMode={true}
                    referenceColumn={column}
                    language={review.reviewLanguage}
                    activeEditor={(review.reasonMarketChanged === true || review.reasonTranslationChanged === true)}
                />
            </div>

            <T.ActiveEditRevisionTitle error={!reasonIsSet}>
                <I18N text="review reason" namespace='warranty' />
                <Icon
                    name={reasonIsSet ? "check" : "asterisk"}
                    color={reasonIsSet ? "@accentGreen" : "@accentRed"}
                    marginTop={3}
                    style={{ marginLeft: 3, marginRight: 0, float: "right" }}
                />
            </T.ActiveEditRevisionTitle>
            <T.SwitchReasonGrid>
                <SwitchButton isActive={toggleBoolOfReview("reasonMarketChanged")} floated='left' activeColor='@accentRed' disabled={review.reasonConfirmed} initialState={review.reasonMarketChanged} />
                <div>
                    <T.SwitchReasonGridIcon shown={review.reasonConfirmed}>
                        <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                    </T.SwitchReasonGridIcon>
                    <T.SwitchReasonGridText active={review.reasonMarketChanged} disabled={review.reasonConfirmed}>Market change</T.SwitchReasonGridText>
                </div>
                <SwitchButton isActive={toggleBoolOfReview("reasonTranslationChanged")} floated='left' activeColor='@accentRed' disabled={review.reasonConfirmed} initialState={review.reasonTranslationChanged} />
                <div>
                    <T.SwitchReasonGridIcon shown={review.reasonConfirmed}>
                        <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                    </T.SwitchReasonGridIcon>
                    <T.SwitchReasonGridText active={review.reasonTranslationChanged} disabled={review.reasonConfirmed}>Translation edited</T.SwitchReasonGridText>
                </div>
                <SwitchButton isActive={toggleBoolOfReview("reasonConfirmed")} floated='left' activeColor='@accentGreen' disabled={review.reasonMarketChanged || review.reasonTranslationChanged} initialState={review.reasonConfirmed} />
                <div>
                    <T.SwitchReasonGridIcon shown={review.reasonMarketChanged || review.reasonTranslationChanged}>
                        <Icon name="lock" style={{ float: "left" }} marginTop={4} color='@accentRed' />
                    </T.SwitchReasonGridIcon>
                    <T.SwitchReasonGridText active={review.reasonConfirmed} disabled={review.reasonMarketChanged || review.reasonTranslationChanged}>Confirmed</T.SwitchReasonGridText>
                </div>
            </T.SwitchReasonGrid>

            <T.ActiveEditRevisionTitle error={(remarkText !== "" && remarkText !== "<p><br></p>") ? false : (review.reasonMarketChanged || review.reasonTranslationChanged)}>
                <I18N text="remark" namespace='warranty' />
                {((review.reasonMarketChanged || review.reasonTranslationChanged)) &&
                    <Icon
                        name={(remarkText === "" || remarkText === "<p><br></p>") ? "asterisk" : "check"}
                        color={(remarkText === "" || remarkText === "<p><br></p>") ? "@accentRed" : "@accentGreen"}
                        marginTop={3}
                        style={{ marginLeft: 3, marginRight: 0, float: "right" }}
                    />
                }
            </T.ActiveEditRevisionTitle>
            <div>
                <Editor
                    value={remarkText}
                    onChange={setRemarkText}
                    showExpandButton={false}
                    showHelpButton={false}
                    showPreviewButton={false}
                    formats={[
                        'bold',
                        'list',
                        'underline',
                        'color',
                    ]}
                />
            </div>
        </T.ActiveEditRevisionContainer>
    );
};

export default WikiActiveReviewPanel;
